import { createTheme, ThemeProvider } from '@mui/material/styles';
import React from 'react';

import { DialogProvider } from './DialogProvider';
import { SnackbarProvider } from './SnackbarProvider';

export const getProviderTheme = (mode: 'light' | 'dark') => createTheme({
    palette: {
        mode,
    },
    components: {
        MuiInputBase: {
            styleOverrides: {
                root: {
                    height: '40px',
                    fontSize: '14px',
                    borderRadius: '6px',
                    // Apply white background and dark text globally in dark mode
                    ...(mode === 'dark' && {
                        backgroundColor: '#fff',
                        color: 'var(--color-cs-900)',
                        '& .MuiSvgIcon-root': {
                            color: 'var(--color-cs-900)',
                        },
                        '& .MuiInputAdornment-root .MuiTypography-root': {
                            color: 'var(--color-cs-900)',
                        },
                    }),
                },
                multiline: {
                    height: 'auto',
                },
                input: {
                    ...(mode === 'dark' && {
                        '&::placeholder': {
                            color: 'var(--color-cs-900)',
                            opacity: 0.6, // Slight opacity for placeholder feel
                        },
                        '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
                            WebkitBoxShadow: '0 0 0 100px #fff inset !important',
                            WebkitTextFillColor: 'var(--color-cs-900) !important',
                            caretColor: 'var(--color-cs-900)',
                            transition: 'background-color 9999s ease-in-out 0s',
                        },
                    }),
                },
            }
        },

        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1a3866',
                        borderWidth: '0.5px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#2b4f8c',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1a3866',
                        borderWidth: '1px',
                    },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#d32f2f',
                    },
                    // Ensure background stays white even when focused or hovered in dark mode
                    ...(mode === 'dark' && {
                        backgroundColor: '#fff',
                    })
                },
            },
        },

        MuiMenu: {
            styleOverrides: {
                list: {
                    paddingTop: '0px',
                    paddingBottom: '0px',
                },
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    padding: '0px',
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    overflow: 'visible',
                },
            }
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    fontSize: '14px',
                    fontWeight: 400,
                }
            }
        },
    },
});

interface SystemThemeProviderProps extends React.PropsWithChildren {
    isDarkMode?: boolean;
}

export const SystemThemeProvider: React.FC<SystemThemeProviderProps> = ({ isDarkMode = false, children }) => {
    // const isDarkMode = useDarkMode()

    return (
        <ThemeProvider theme={getProviderTheme(isDarkMode ? 'dark' : 'light')}>
            <SnackbarProvider>
                <DialogProvider>
                    {children}
                </DialogProvider>
            </SnackbarProvider>
        </ThemeProvider>
    )
}
